/* eslint no-mixed-operators: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import moment from 'moment';
import lodash from 'lodash';
import classnames from 'classnames';
import AccessorialRequestActionCreators from 'actions/AccessorialRequestActionCreators';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import ExceptionStore from 'stores/ExceptionStore';
import SessionStore from 'stores/SessionStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { FileText, X, Info } from 'lucide-react';
import {
  Tooltip as RadixTooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import DataPoller from 'components/ui/DataPoller';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getShipmentTimezone, isShipmentCanceled } from 'helpers/shipments';
import formatMoney from 'utils/formatters/Money';
import CargomaticAlert from 'components/ui/Alert';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';
import { getAccessorialRequestsAutoDeclineDates } from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import AccessorialDocumentUploadModal from './AccessorialDocumentUploadModal';
import Spinner from '../../ui/Spinner';
import AddButton from '../../v2/buttons/AddButton';
import AccessorialRequestStatusBadge from '../../v2/AccessorialRequestStatusBadge';
import CargomaticModal from '../../v2/modal';
import AccessorialRequestForm from './AccessorialRequestForm';
import { sluggify } from '../../../utils/localization/utils';
import { withLocaleContext } from 'providers/locale-settings-context';

const Loading = () => (
  <div className="w-full">
    <div className="">
      <span className="justify-center text-5xl text-green">
        <Spinner showMessage={false} />
      </span>
    </div>
  </div>
);

const VIEW_HEADERS = [
  'ACCESSORIAL',
  'TOTAL',
  'ENTERED',
  'STATUS',
  'REMARK',
  'UPDATED',
];

if (SessionStore.isCarrier()) {
  VIEW_HEADERS.push('MISSING_DOCUMENTS_QUESTION');
}

const POLL_INTERVAL = 8000;

class ViewAccessorialRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      loading: true,
      showUploadModal: false,
      selectedAccessorial: null,
    };
  }

  componentDidMount() {
    AccessorialRequestActionCreators.getAccessorialRequests(this.getRequest());
    ExceptionStore.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    ExceptionStore.removeChangeListener(this.onChange);
  }

  getRequest() {
    if (SessionStore.isCarrier()) {
      const carrier = this.props.job.carrier;

      return {
        shipment: this.props.job._id,
        payee_type: 'carrier',
        carrier,
      };
    }
    return {
      shipment: this.props.job._id,
      shipper_status: 'approved',
    };
  }
  onChange = async () => {
    try {
      const { updates } = this.state;
      const { isShipper, isCarrier, job } = this.props;
      let exceptions = ExceptionStore.getAllAccessorialRequests();

      if (isShipper) {
        exceptions = lodash.filter(exceptions, (exception) => {
          if (!exception.shipment) {
            return false;
          }
          return (
            exception.shipment._id === job._id &&
            exception.shipper_status !== 'pending-admin'
          );
        });
      } else if (isCarrier) {
        exceptions = lodash.filter(exceptions, (exception) => {
          if (!exception.shipment || !exception.carrier_cost) {
            return false;
          }
          return (
            exception.shipment._id === job._id &&
            (parseFloat(exception.carrier_cost) > 0.0 ||
              parseFloat(exception.carrier_cost) < 0.0)
          );
        });
      } else {
        exceptions = lodash.filter(exceptions, (exception) => {
          if (!exception.shipment) {
            return false;
          }
          return exception.shipment._id === job._id;
        });
      }

      let units;
      let rate;
      let total;

      const accessorialRequestIdsMissingDocs = exceptions
        .filter((e) => e.missing_docs)
        .map((e) => e._id);
      let accessorialAutoDeclineDates = {};
      if (
        featureEnabled(CONFIGURED_FEATURES.SSR) &&
        accessorialRequestIdsMissingDocs &&
        accessorialRequestIdsMissingDocs.length
      ) {
        accessorialAutoDeclineDates = await this.getShipperAutoDeclineDates(
          accessorialRequestIdsMissingDocs,
        );
      }
      const rows = exceptions.map((item) => {
        if (isShipper) {
          units = item.shipper_unit_qty || 0;
          rate = item.shipper_rate || 0;
        } else {
          units = item.carrier_unit_qty || 0;
          rate = item.carrier_rate || 0;
        }
        total = units * rate;
        const shipperAutoDeclineDate =
          accessorialAutoDeclineDates[item._id.toString()];
        const mapExceptionsToTable = {
          _id: item?._id,
          accessorial: item.message,
          accessorial_type: item?.accessorial_type,
          status: isShipper ? item.shipper_status : item.carrier_status,
          updated: item.modified_at,
          units: `${units}`,
          created: item.created_at,
          id: item._id,
          shipper_rate: item.shipper_rate || 0,
          shipper_status: item.shipper_status,
          carrier_status: item.carrier_status,
          shipment: item.shipment,
          carrier_note:
            (item.carrier_status === 'declined' &&
              item.carrier_decline_reason_code) ||
            item.carrier_note ||
            '',
          missing_docs: item.missing_docs,
          rate,
          total,
          shipperAutoDeclineDate,
        };
        return Object.assign(mapExceptionsToTable);
      });
      this.setState({
        loading: false,
        updates,
        rows,
      });
    } catch (e) {
      console.error('Error on accessorial request change: ', e.message);
    }
  };

  getShipperAutoDeclineDates = async (accessorialRequestIds) => {
    const { t } = this.props;
    try {
      const result = await getAccessorialRequestsAutoDeclineDates(
        accessorialRequestIds,
      );
      const items = result?.entity?.items || [];

      return items.reduce((dates, { _id, shipper_auto_decline_date }) => {
        dates[_id] = shipper_auto_decline_date;
        return dates;
      }, {});
    } catch (e) {
      showToast(
        t('ACCESSORIAL_REQUEST_ERROR'),
        t('ERROR_RETRIEVING_SHIPPER_ACCESSORIAL_REQUEST_AUTO_DECLINE_DATE'),
        'error',
      );
      console.error(
        `Error retrieving shipper accessorial request auto decline date: ${e.message}`,
      );
      return {};
    }
  };

  removeAccessorial = (accessorial) => {
    const id = accessorial.id;
    const exceptions = ExceptionStore.getAllAccessorialRequests();
    const toDelete = _.findWhere(exceptions, { _id: id });
    if (toDelete) {
      this.props.removeAccessorial(toDelete);
    }
  };

  showUploadModal = (accessorial) => {
    this.setState({ showUploadModal: true, selectedAccessorial: accessorial });
  };

  hideUploadModal = () => {
    this.setState({ showUploadModal: false, selectedAccessorial: null });
  };

  render() {
    const { rows, loading } = this.state;
    const { isShipper } = this.props;

    if (loading) {
      return <Loading />;
    }
    return (
      <div className="w-full">
        <DataPoller
          frequency={POLL_INTERVAL}
          action={() => {
            AccessorialRequestActionCreators.getAccessorialRequests(
              this.getRequest(),
            );
          }}
        />
        <div className="flex flex-wrap">
          <ViewReqTable
            shipper={isShipper}
            headers={VIEW_HEADERS}
            rows={rows}
            job={this.props.job}
            removeAccessorial={this.removeAccessorial}
            showUploadModal={this.showUploadModal}
            t={this.props.t}
            formatDateTime={this.props.formatDateTime}
          />
        </div>
        <div>
          {this.state.showUploadModal && (
            <AccessorialDocumentUploadModal
              show={this.state.showUploadModal}
              onHide={this.hideUploadModal}
              shipment={this.props.job}
              accessorial={this.state.selectedAccessorial}
            />
          )}
        </div>
      </div>
    );
  }
}

ViewAccessorialRequests.propTypes = {
  isShipper: PropTypes.bool,
  job: PropTypes.object,
  removeAccessorial: PropTypes.func,
};

ViewAccessorialRequests.defaultProps = {
  isShipper: false,
  job: {},
  removeAccessorial: () => {
    return null;
  },
};

class ViewReqTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showConfirmDeclineModal: false,
      accessorialToDecline: {},
      showAccessorialRequestModal: false,
      hasMissingDocuments: (props.rows || []).some((row) => row.missing_docs),
    };
  }

  handleShowAccessorialRequestModal = () => {
    this.setState({ showAccessorialRequestModal: true });
  };

  handleHideAccessorialRequestModal = () => {
    this.setState({ showAccessorialRequestModal: false });
  };

  handleSaveAccessorialRequestSuccess = () => {
    this.handleHideAccessorialRequestModal();
    this.fetchAccessorialRequests();
  };

  fetchAccessorialRequests() {
    AccessorialRequestActionCreators.getAccessorialRequests(this.getRequest());
  }

  onDecline = async () => {
    const {
      accessorialToDecline: {
        id: _id,
        shipment: { _id: jobId },
      },
      accessorialToDecline,
    } = this.state;
    const accessorials = {
      _id,
      declineCarrier: true,
      declineShipper: true,
      ...accessorialToDecline,
    };
    const params = {
      sendEmail: false,
      accessorials: [accessorials],
      resolution_type: 'decline',
      jobId,
    };

    const user = SessionStore.getUser();
    if (user._type === 'Carrier') {
      const carrierPartner = SessionStore.getCarrierPartner();
      if (carrierPartner && user._id !== carrierPartner) {
        params.carrierPartner = carrierPartner;
      }
    }
    const result = await ShipmentWebAPIUtils.declineAccessorial(params);
    if (result) {
      this.setState({
        showConfirmDeclineModal: false,
      });
      this.fetchAccessorialRequests();
    }
  };

  removeSelectedAccessorial = (accessorial) => {
    const { removeAccessorial, shipper: isShipper } = this.props;
    if (isShipper) {
      removeAccessorial(accessorial);
    } else {
      this.setState({
        accessorialToDecline: { ...accessorial },
        showConfirmDeclineModal: true,
      });
    }
  };

  canDecline = (accessorial) => {
    const { carrier_status, shipper_rate, shipper_status } = accessorial;
    if (shipper_status === 'approved') return false;
    const nonZeroShipperRate = shipper_rate && shipper_rate >= 0;
    return !nonZeroShipperRate && carrier_status === 'pending-admin';
  };

  renderAccessorialMissingDocumentsError() {
    const { t } = this.props;
    const { hasMissingDocuments } = this.state;

    if (featureEnabled(CONFIGURED_FEATURES.SSR) && SessionStore.isCarrier()) {
      if (hasMissingDocuments) {
        return (
          <CargomaticAlert className="my-4" type="error">
            {t('ACCESSORIALS_WITH_MISSING_DOCUMENTS')}
          </CargomaticAlert>
        );
      }
    }

    return null;
  }

  renderColumns = (accessorial) => {
    const buttonClassName = {
      button: true,
    };
    let { status } = accessorial;
    const {
      accessorial: acc,
      rate,
      units,
      unit_name,
      total,
      created,
      updated,
      id,
      carrier_note,
      currency,
      shipperAutoDeclineDate,
    } = accessorial;
    const { shipper, t, formatDateTime } = this.props;
    const accessorialKey = `accessorial_${id}`;
    const totalKey = `total_${id}`;
    const createdKey = `created_${id}`;
    const updatedKey = `updated_${id}`;
    const statusKey = `status_${id}`;
    const tooltipDocText = t('ADD_DOCUMENT');
    const tooltipCancelText = t('CANCEL_REQUEST');
    if (status.indexOf('pending-') > -1) {
      status = 'pending';
    }

    const actionColumn = [
      <td
        key="action-upload"
        className="px-6 py-4 font-medium whitespace-nowrap"
      >
        <button
          type="button"
          className={classnames(buttonClassName)}
          onClick={() => this.props.showUploadModal(accessorial)}
          style={{ padding: 0 }}
          disabled={status === 'declined'}
        >
          <TooltipProvider>
            <RadixTooltip>
              <TooltipTrigger asChild>
                <FileText className="h-4 w-4" style={{ marginRight: '2px' }} />
              </TooltipTrigger>
              <TooltipContent side="top">{tooltipDocText}</TooltipContent>
            </RadixTooltip>
          </TooltipProvider>
        </button>
        <TooltipProvider>
          <RadixTooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={classnames(buttonClassName)}
                onClick={() => this.removeSelectedAccessorial(accessorial)}
                disabled={!this.canDecline(accessorial)}
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{tooltipCancelText}</TooltipContent>
          </RadixTooltip>
        </TooltipProvider>
      </td>,
    ];

    const newColumnData = [
      <td
        className="py-4 text-sm border-b border-solid border-grey-lighter text-black-500"
        key={accessorialKey}
      >
        {titleize(t(sluggify(acc)))}
      </td>,
      <td
        className="py-4 text-sm border-b border-solid border-grey-lighter text-black-500"
        key={totalKey}
      >
        <div className="flex flex-wrap">
          <div>{formatMoney(total, currency)}</div>
          {unit_name === 'flat' && <span>&nbsp;(Flat)</span>}
          {units > 1 && [
            <div key="rate"> &nbsp;({formatMoney(rate, currency)}</div>,
            <span key="x">&nbsp;X&nbsp;</span>,
            <div key="units">{units}</div>,
            <span key="paren">)</span>,
          ]}
        </div>
      </td>,
      <td
        className="py-4 text-sm border-b border-solid border-grey-lighter text-black-500"
        style={{ width: '50px' }}
        key={createdKey}
      >
        {formatDateTime(created, DEFAULT_TIMEZONE)}
      </td>,
      <td
        className="text-sm border-b border-solid rounded border-grey-lighter text-black-500"
        key={statusKey}
      >
        <AccessorialRequestStatusBadge status={status} />
      </td>,
      <td
        className="py-4 text-sm border-b border-solid border-grey-lighter text-black-500"
        style={{ width: '50px' }}
        key={updatedKey}
      >
        {formatDateTime(updated, DEFAULT_TIMEZONE)}
      </td>,
    ];
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
      const { job } = this.props;

      if (!(shipperAutoDeclineDate && job)) {
        newColumnData.push(<td />);
      } else {
        const timezone = getShipmentTimezone(job);
        newColumnData.push(
          <td
            className="py-4 text-sm border-b border-solid border-grey-lighter text-red-700"
            style={{ width: '150px' }}
          >
            <div className="flex justify-between">
              <ExclamationIcon className="w-6 h-6 m-auto" />
              <p className="pl-1">{`Due on ${moment.tz(shipperAutoDeclineDate, timezone).format('L')}`}</p>
            </div>
          </td>,
        );
      }
    }
    if (!shipper) {
      newColumnData.push(actionColumn);
      newColumnData
        .splice(
          4,
          0,
          <td
            className="py-4 text-sm border-b border-solid border-grey-lighter text-black-500"
            style={{ width: '150px' }}
            key={updatedKey}
          >
            {carrier_note}
          </td>,
        )
        .join();
    }
    return newColumnData;
  };

  renderRow(row) {
    const columns = this.renderColumns(row);
    return (
      <tr key={row.id} className="p-1">
        {columns}
      </tr>
    );
  }

  VIEW_REQ_TOOLTIPS = {
    STATUS: (
      <ol>
        <li>{this.props.t('VIEW_REQ_TOOLTIP_1')}</li>
        <li>{this.props.t('VIEW_REQ_TOOLTIP_2')}</li>
        <li>
          {this.props.shipper
            ? this.props.t('VIEW_REQ_TOOLTIP_3_A')
            : this.props.t('VIEW_REQ_TOOLTIP_3_B')}
        </li>
        <li>{this.props.t('VIEW_REQ_TOOLTIP_4')}</li>
      </ol>
    ),
  };

  renderHeader = () => {
    let columnHeaders = this.props.headers;
    if (this.props.shipper) {
      columnHeaders = columnHeaders.filter((el) => el !== 'REMARK');
    }
    const { t } = this.props;
    return (
      <thead>
        <tr key="accessorial-headers">
          {columnHeaders.map((col) => {
            if (this.VIEW_REQ_TOOLTIPS[col]) {
              return (
                <th key={col} className="text-sm font-bold text-grey-darker">
                  {t(col)}
                  <TooltipProvider>
                    <RadixTooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 pl-1 text-grey-darker inline-block" />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {this.VIEW_REQ_TOOLTIPS[col]}
                      </TooltipContent>
                    </RadixTooltip>
                  </TooltipProvider>
                </th>
              );
            }
            return (
              <th key={col} className="text-sm font-bold text-grey-darker">
                {t(col)}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  renderBody() {
    const { rows } = this.props;

    return <tbody>{rows.map((row) => this.renderRow(row))}</tbody>;
  }

  render() {
    const { title, rows, shipper, t, job } = this.props;

    return (
      <div className="w-full">
        <div className="flex justify-between">
          <h4 className="mb-2 text-grey-darker">{t(title)}</h4>
          {!shipper && (
            <AddButton
              onClick={this.handleShowAccessorialRequestModal}
              disabled={isShipmentCanceled(job)}
            />
          )}
        </div>
        {featureEnabled(CONFIGURED_FEATURES.SSR) &&
          this.renderAccessorialMissingDocumentsError()}
        <hr />
        <div className="stop-rows">
          <table className="table">
            {this.renderHeader()}
            {rows && rows.length > 0 ? (
              this.renderBody()
            ) : (
              <tbody>
                <tr>
                  <td
                    colSpan={this.props.headers.length}
                    className="py-4 text-sm text-grey-darker text-center"
                  >
                    {t('NO_ACCESSORIAL_REQUESTS')}
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          <ConfirmModal
            show={this.state.showConfirmDeclineModal}
            title={t('DECLINE')}
            message={t('ACCESSORIAL_REQUEST_DECLINE')}
            onHide={() => this.setState({ showConfirmDeclineModal: false })}
            onConfirm={this.onDecline}
          />
        </div>
        {this.state.showAccessorialRequestModal && (
          <CargomaticModal
            show={this.state.showAccessorialRequestModal}
            onHide={this.handleHideAccessorialRequestModal}
            title={`${t('CREATE_ACCESSORIAL_REQUEST')}`}
          >
            <AccessorialRequestForm
              job={this.props.job}
              accessorialRequest={this.state.selectedAccessorial}
              existingRequests={ExceptionStore.getAllAccessorialRequests()}
              onSaveSuccess={this.handleSaveAccessorialRequestSuccess}
              onCancel={this.handleHideAccessorialRequestModal}
            />
          </CargomaticModal>
        )}
      </div>
    );
  }
}

ViewReqTable.propTypes = {
  title: PropTypes.string,
  shipper: PropTypes.bool,
  headers: PropTypes.array,
  rows: PropTypes.array,
  removeAccessorial: PropTypes.func,
  showUploadModal: PropTypes.func,
};

ViewReqTable.defaultProps = {
  headers: [],
  rows: [],
  title: 'ACCESSORIAL_REQUESTS',
  showUploadModal: () => {
    return null;
  },
  shipper: false,
  removeAccessorial: () => {
    return null;
  },
};

export default withTranslation()(withLocaleContext(ViewAccessorialRequests));
